'use client';

import {
    useSingleTypeEditor,
    UseSingleTypeEditorType
} from "@/app/lib/hooks/use-editor/useSingleTypeEditor";
import { SitePhotoArchive, SitePhotoArchiveInput } from "@/app/lib/generated/graphql-types";
import { createContext, ReactNode, useContext } from "react";
import FloatingEditorMenu from "@/app/features/shared/__to_be_integrated/FloatingEditorMenu";
import LoadingSpinner from "@/app/__to_be_integrated/_____components/_NEW/LoadingSpinner";
import { toggleDraftMode } from "@/app/lib/actions/editing";
import { getSitePhotoArchive } from "@/app/features/sites/photo-archive/data-access/queries";
import { updateSitePhotoArchive } from "@/app/features/sites/photo-archive/data-access/mutations";
import { SitePhotoArchiveSchema } from "@/app/features/sites/photo-archive/types/schema";
import { SitePhotoArchiveEditable } from "@/app/features/sites/photo-archive/types/types";

const SitePhotoArchiveContext = createContext<UseSingleTypeEditorType<SitePhotoArchiveEditable> | undefined>(undefined);

export function useSitePhotoArchiveEditor() {
    const context = useContext(SitePhotoArchiveContext);
    if (!context) {
        throw new Error("useSitePhotoArchiveEditor must be used within a SitePhotoArchiveEditorProvider");
    }
    return context;
}

interface SitePhotoArchiveEditorProviderProps {
    children: ReactNode;
    isDraftModeEnabled: boolean;
}

export default function SitePhotoArchiveEditorProvider({
    children,
    isDraftModeEnabled,
}: SitePhotoArchiveEditorProviderProps) {
    const editor = useSingleTypeEditor<SitePhotoArchive, SitePhotoArchiveEditable, SitePhotoArchiveInput>({
        schema: SitePhotoArchiveSchema,
        loadEntity: getSitePhotoArchive,
        update: updateSitePhotoArchive,
    });

    if (editor.isLoading) return <LoadingSpinner />;

    return (
        <SitePhotoArchiveContext.Provider value={editor}>
            {children}
            <FloatingEditorMenu
                autosaveState={editor.autosaveState}
                isDraftModeEnabled={isDraftModeEnabled}
                toggleDraftMode={toggleDraftMode}
                hasChanges={editor.hasChanges}
                onPublish={editor.publish}
                onDiscard={editor.discardChanges}
            />
        </SitePhotoArchiveContext.Provider>
    );
}
